// /api/templates hooks — message-draft system-prompt overrides.

import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { MessageKind, PutTemplateRequest } from "@resume-cloudflare/shared";
import { api, ApiError } from "./api";
import { useMutationToast } from "../hooks/useMutationToast";

export interface TemplateRow {
  kind: MessageKind;
  label: string;
  active_system_prompt: string;
  default_system_prompt: string;
  is_overridden: boolean;
  updated_at: number | null;
}

export function useTemplates() {
  return useQuery({
    queryKey: ["templates"],
    queryFn: () => api<{ templates: TemplateRow[] }>("/api/templates"),
    staleTime: 10_000,
  });
}

export function usePutTemplate(kind: MessageKind) {
  const qc = useQueryClient();
  return useMutationToast<unknown, ApiError, PutTemplateRequest>({
    mutationFn: (input) =>
      api(`/api/templates/${kind}`, {
        method: "PUT",
        body: JSON.stringify(input),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["templates"] }),
    success: "Template saved.",
    errorPrefix: "Save failed",
  });
}

export function useDeleteTemplate(kind: MessageKind) {
  const qc = useQueryClient();
  return useMutationToast<unknown, ApiError, void>({
    mutationFn: () => api(`/api/templates/${kind}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["templates"] }),
    success: "Reverted to default.",
    errorPrefix: "Reset failed",
  });
}
